import Phaser from "phaser";
import type { EventBus, MinigameDelta } from "../engine/EventBus";
import type { Blackboard } from "../engine/Blackboard";
import { tagValue, untagValue } from "../engine/Blackboard";
import { ScopedLoader } from "./ScopedLoader";
import { resolveAssetUrl } from "../engine/assetRoot";

const MAX_GAME_OBJECTS = 500;

export interface MinigameModule {
  create(host: MinigameHost, config: Record<string, unknown>): void;
  update?(time: number, delta: number): void;
  destroy?(): void;
}

export class MinigameHost {
  readonly width = 1280;
  readonly height = 720;
  readonly keys: Phaser.Input.Keyboard.KeyboardPlugin;
  readonly pointer: Phaser.Input.Pointer;

  private _scene: Phaser.Scene;
  private _bus: EventBus;
  private _bb: Blackboard;
  private _loader: ScopedLoader;
  private _minigameId: string;
  private _objects: Phaser.GameObjects.GameObject[] = [];
  private _tweens: Phaser.Tweens.Tween[] = [];
  private _timers: Phaser.Time.TimerEvent[] = [];
  private _completed = false;
  private _pendingDelta: MinigameDelta | null = null;
  private _onComplete: (result: string, delta?: MinigameDelta) => void;

  constructor(
    scene: Phaser.Scene,
    bus: EventBus,
    bb: Blackboard,
    minigameId: string,
    onComplete: (result: string, delta?: MinigameDelta) => void,
  ) {
    this._scene = scene;
    this._bus = bus;
    this._bb = bb;
    this._minigameId = minigameId;
    this._loader = new ScopedLoader(scene, minigameId);
    this._onComplete = onComplete;
    this.keys = scene.input.keyboard!;
    this.pointer = scene.input.activePointer;
  }

  get scene(): Phaser.Scene {
    return this._scene;
  }

  /** The registry ID used to launch this minigame (e.g. "tri_peaks", "blackjack"). */
  get minigameId(): string {
    return this._minigameId;
  }

  private _trackObject<T extends Phaser.GameObjects.GameObject>(obj: T): T {
    if (this._objects.length >= MAX_GAME_OBJECTS) {
      throw new Error(`MinigameHost: max game objects (${MAX_GAME_OBJECTS}) exceeded`);
    }
    this._objects.push(obj);
    return obj;
  }

  addImage(x: number, y: number, key: string): Phaser.GameObjects.Image {
    return this._trackObject(this._scene.add.image(x, y, this._loader.prefixKey(key)));
  }

  addText(x: number, y: number, text: string, style?: Phaser.Types.GameObjects.Text.TextStyle): Phaser.GameObjects.Text {
    return this._trackObject(this._scene.add.text(x, y, text, style));
  }

  addRectangle(x: number, y: number, w: number, h: number, color: number, alpha?: number): Phaser.GameObjects.Rectangle {
    return this._trackObject(this._scene.add.rectangle(x, y, w, h, color, alpha));
  }

  addSprite(x: number, y: number, key: string): Phaser.GameObjects.Sprite {
    return this._trackObject(this._scene.add.sprite(x, y, this._loader.prefixKey(key)));
  }

  loadImage(key: string, url: string): Promise<void> {
    return this._loader.loadImage(key, url);
  }

  loadSpritesheet(key: string, url: string, frameWidth: number, frameHeight: number): Promise<void> {
    return this._loader.loadSpritesheet(key, url, frameWidth, frameHeight);
  }

  loadAudio(key: string, url: string): Promise<void> {
    return this._loader.loadAudio(key, url);
  }

  async loadJSON(url: string): Promise<unknown> {
    const resp = await fetch(resolveAssetUrl(url));
    if (!resp.ok) {
      throw new Error(`MinigameHost.loadJSON: HTTP ${resp.status} for "${url}"`);
    }
    return resp.json() as Promise<unknown>;
  }

  prefixKey(key: string): string {
    return this._loader.prefixKey(key);
  }

  /** Mark a loaded texture most-recently-used so LRU eviction spares it. */
  touchTexture(key: string): void {
    this._loader.touch(key);
  }

  /**
   * Replace the protected texture working set — pinned textures are never
   * evicted under memory pressure. Pass the currently-visible and one-step-
   * reachable keys (e.g. a Passages viewpoint + its turn/forward frames).
   */
  pinTextures(keys: Iterable<string>): void {
    this._loader.pin(keys);
  }

  getTextureSize(key: string): { width: number; height: number } | null {
    const prefixed = this._loader.prefixKey(key);
    if (!this._scene.textures.exists(prefixed)) return null;
    const src = this._scene.textures.get(prefixed).getSourceImage() as HTMLImageElement;
    return { width: src.naturalWidth || src.width, height: src.naturalHeight || src.height };
  }

  playSoundDirect(key: string, options?: Phaser.Types.Sound.SoundConfig): void {
    const prefixed = this._loader.prefixKey(key);
    if (this._scene.cache.audio.has(prefixed)) {
      this._scene.sound.play(prefixed, options);
    }
  }

  playSound(key: string, config?: { loop?: boolean; volume?: number }): void {
    const prefixed = this._loader.prefixKey(key);
    this._bus.emit("audio.play", {
      channel: "sfx",
      asset: prefixed,
      loop: config?.loop,
    });
  }

  stopSound(key: string): void {
    const prefixed = this._loader.prefixKey(key);
    this._bus.emit("audio.stop", { channel: "sfx" });
    void prefixed;
  }

  addTween(config: Phaser.Types.Tweens.TweenBuilderConfig): Phaser.Tweens.Tween {
    const tween = this._scene.tweens.add(config);
    this._tweens.push(tween);
    return tween;
  }

  addTimer(config: Phaser.Types.Time.TimerEventConfig): Phaser.Time.TimerEvent {
    const timer = this._scene.time.addEvent(config);
    this._timers.push(timer);
    return timer;
  }

  getVar(key: string): unknown {
    const bbKey = `_mg_${this._minigameId}_${key}`;
    return untagValue(this._bb.get(bbKey));
  }

  setVar(key: string, value: unknown): void {
    const bbKey = `_mg_${this._minigameId}_${key}`;
    this._bb.set(bbKey, tagValue(value as string | number | boolean));
  }

  /**
   * Stage an inventory delta to apply once on completion (combat design §6).
   * The host applies it outside the sandbox after `complete()`/`cancel()`; safe
   * to call multiple times (last wins) before completing.
   */
  commitDelta(delta: MinigameDelta): void {
    this._pendingDelta = delta;
  }

  complete(result: "success" | "failure"): void {
    if (this._completed) return;
    this._completed = true;
    this._onComplete(result, this._pendingDelta ?? undefined);
  }

  cancel(): void {
    if (this._completed) return;
    this._completed = true;
    this._onComplete("cancelled", this._pendingDelta ?? undefined);
  }

  get isCompleted(): boolean {
    return this._completed;
  }

  destroy(): void {
    for (const tween of this._tweens) {
      if (tween.isPlaying()) tween.stop();
    }
    this._tweens = [];
    for (const timer of this._timers) {
      timer.remove();
    }
    this._timers = [];
    for (const obj of this._objects) {
      obj.destroy();
    }
    this._objects = [];
    this._loader.destroy();
  }
}
